import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, Save, Trash2, X } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { useAuth } from "@/stores/auth";
import { ExcludesApi, type ExcludeSetRow } from "@/lib/services";
import { fa } from "@/locales/fa";
import { toast } from "sonner";

const PERM = "excludes.manage";

export function ExcludesPage() {
  const qc = useQueryClient();
  const canManage = useAuth((s) => s.hasPerm(PERM));
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [draft, setDraft] = useState<Partial<ExcludeSetRow>>({});

  const { data: sets = [], isLoading } = useQuery({
    queryKey: ["excludes"],
    queryFn: () => ExcludesApi.list(),
    refetchInterval: 15_000,
  });

  const selected = sets.find((s) => s.id === selectedId) ?? null;

  // Mutation takes the patch as variables so the network call uses the data
  // from the click event itself, not whatever `draft` happens to be in the
  // current closure. The previous implementation called `setDraft(patch)`
  // and then `create.mutate()` synchronously — the mutationFn closed over
  // the *previous* render's `draft` (still empty for a brand-new set), so
  // the very first save created an empty "new-set" with no patterns.
  const create = useMutation({
    mutationFn: (patch: Partial<ExcludeSetRow>) =>
      ExcludesApi.create({
        name: patch.name?.trim() || "new-set",
        description: patch.description ?? null,
        patterns: patch.patterns ?? [],
      }),
    onSuccess: (row) => {
      toast.success(fa.excludes.toastCreated);
      void qc.invalidateQueries({ queryKey: ["excludes"] });
      setSelectedId(row.id);
      setDraft({});
    },
    onError: (e) => toast.error(String(e instanceof Error ? e.message : e)),
  });

  const update = useMutation({
    mutationFn: (input: { id: string; patch: Partial<ExcludeSetRow> }) =>
      ExcludesApi.update(input.id, {
        name: input.patch.name,
        description: input.patch.description ?? undefined,
        patterns: input.patch.patterns,
      }),
    onSuccess: () => {
      toast.success(fa.excludes.toastSaved);
      void qc.invalidateQueries({ queryKey: ["excludes"] });
    },
    onError: (e) => toast.error(String(e instanceof Error ? e.message : e)),
  });

  const remove = useMutation({
    mutationFn: (id: string) => ExcludesApi.remove(id),
    onSuccess: () => {
      toast.success(fa.excludes.toastDeleted);
      void qc.invalidateQueries({ queryKey: ["excludes"] });
      setSelectedId(null);
    },
    onError: (e) => toast.error(String(e instanceof Error ? e.message : e)),
  });

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold tracking-tight">{fa.excludes.title}</h1>
        {canManage && (
          <Button
            onClick={() => {
              setSelectedId(null);
              setDraft({ name: "", patterns: [] });
            }}
            disabled={create.isPending}
          >
            <Plus className="ms-2 h-4 w-4" />
            {fa.excludes.newSet}
          </Button>
        )}
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[320px_1fr]">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">{fa.excludes.sets}</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col gap-1">
            {isLoading && <div className="text-sm text-muted-foreground">…</div>}
            {sets.map((s) => (
              <button
                key={s.id}
                type="button"
                onClick={() => {
                  setSelectedId(s.id);
                  setDraft({});
                }}
                className={`flex items-center justify-between gap-2 rounded-md px-2 py-2 text-start text-sm hover:bg-accent ${
                  selectedId === s.id ? "bg-accent" : ""
                }`}
              >
                <div className="flex flex-col">
                  <span className="font-medium">{s.name}</span>
                  <span className="text-xs text-muted-foreground">
                    {fa.excludes.patternCount(s.patterns.length)} ·{" "}
                    {/* The synthetic agent-defaults entry applies to every
                        backup implicitly — it isn't attached to any Policy
                        row in the DB, so `policyCount` is always 0. Rendering
                        "استفاده در 0 قانون" misleads operators into thinking
                        it's an unused / dead set; show its true scope instead. */}
                    {s.id === "__agent_defaults__"
                      ? "اعمال خودکار روی همهٔ بکاپ‌ها"
                      : fa.excludes.usedBy(s.policyCount)}
                  </span>
                </div>
                {s.isSystem && (
                  // The 320px sidebar can squeeze this Badge until the
                  // Persian "پیش‌فرض سیستم" wraps onto a second line at
                  // the ZWNJ. `whitespace-nowrap` keeps it on a single
                  // line, and `shrink-0` stops flex from carving width
                  // away from the badge in favour of the long name+meta
                  // column to its left.
                  <Badge variant="secondary" className="shrink-0 whitespace-nowrap">
                    {fa.excludes.system}
                  </Badge>
                )}
              </button>
            ))}
            {sets.length === 0 && !isLoading && (
              <div className="rounded-md border border-dashed p-4 text-center text-sm text-muted-foreground">
                {fa.excludes.empty}
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">
              {selected ? selected.name : fa.excludes.editor}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Editor
              key={selected?.id ?? "new"}
              initial={selected ?? draft}
              readOnly={!canManage || (selected?.isSystem ?? false)}
              onSave={(patch) => {
                if (selected) update.mutate({ id: selected.id, patch });
                else create.mutate(patch);
              }}
              onDelete={
                canManage && selected && !selected.isSystem
                  ? () => {
                      if (window.confirm(fa.excludes.confirmDelete(selected.name))) {
                        remove.mutate(selected.id);
                      }
                    }
                  : undefined
              }
            />
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function Editor(props: {
  initial: Partial<ExcludeSetRow>;
  readOnly: boolean;
  onSave: (patch: Partial<ExcludeSetRow>) => void;
  onDelete?: () => void;
}) {
  const [name, setName] = useState(props.initial.name ?? "");
  const [description, setDescription] = useState(props.initial.description ?? "");
  const [patterns, setPatterns] = useState<string[]>(props.initial.patterns ?? []);
  const [newPattern, setNewPattern] = useState("");

  return (
    <div className="flex flex-col gap-4">
      <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
        <div className="flex flex-col gap-1">
          <label className="text-xs font-medium text-muted-foreground">
            {fa.excludes.name}
          </label>
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            readOnly={props.readOnly}
            disabled={props.readOnly}
            maxLength={120}
          />
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-xs font-medium text-muted-foreground">
            {fa.excludes.description}
          </label>
          <Input
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            readOnly={props.readOnly}
            disabled={props.readOnly}
            maxLength={500}
          />
        </div>
      </div>

      <Separator />

      <div className="flex flex-col gap-2">
        <label className="text-xs font-medium text-muted-foreground">
          {fa.excludes.patterns} ({patterns.length})
        </label>
        <div className="flex flex-col gap-1">
          {patterns.map((p, i) => (
            <div key={`${p}-${i}`} className="flex items-center gap-2">
              <Input
                value={p}
                onChange={(e) => {
                  const next = patterns.slice();
                  next[i] = e.target.value;
                  setPatterns(next);
                }}
                readOnly={props.readOnly}
                disabled={props.readOnly}
                className="font-mono text-xs"
              />
              {!props.readOnly && (
                <Button
                  size="icon"
                  variant="ghost"
                  onClick={() => setPatterns(patterns.filter((_, j) => j !== i))}
                  aria-label={fa.excludes.removePattern}
                >
                  <X className="h-4 w-4" />
                </Button>
              )}
            </div>
          ))}
          {!props.readOnly && (
            <div className="flex items-center gap-2 pt-1">
              <Input
                value={newPattern}
                placeholder={fa.excludes.patternPlaceholder}
                onChange={(e) => setNewPattern(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && newPattern.trim()) {
                    setPatterns([...patterns, newPattern.trim()]);
                    setNewPattern("");
                  }
                }}
                className="font-mono text-xs"
              />
              <Button
                size="sm"
                variant="secondary"
                onClick={() => {
                  if (newPattern.trim()) {
                    setPatterns([...patterns, newPattern.trim()]);
                    setNewPattern("");
                  }
                }}
              >
                <Plus className="h-4 w-4" />
              </Button>
            </div>
          )}
        </div>
      </div>

      {!props.readOnly && (
        <div className="flex items-center justify-between">
          <Button
            variant="default"
            onClick={() => props.onSave({ name, description, patterns })}
            disabled={!name.trim()}
          >
            <Save className="ms-2 h-4 w-4" /> {fa.excludes.save}
          </Button>
          {props.onDelete && (
            <Button variant="destructive" onClick={props.onDelete}>
              <Trash2 className="ms-2 h-4 w-4" /> {fa.excludes.delete}
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
